import { zodResolver } from '@hookform/resolvers/zod';
import { type Requisition, type User } from '@kalos/kalos-rpc';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Combobox,
  DateInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SimpleSelect,
  Textarea,
} from '@kalos/ui';
import { Check, Copy, Minus, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { BusinessAutocompleteInput } from '../../../components/BusinessAutocompleteInput';
import { useAuth } from '../../../context/AuthContext';
import { Flagged } from '../../../featureFlags/Flagged';
import { useFeatureFlag } from '../../../featureFlags/useFeatureFlag';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { TechniciansComboboxPicker } from '../../../modules/ComponentsLibrary/Pickers/newPickers/TechnicianComboboxPicker';
import {
  buildRequisition,
  findCatalogItem,
  formatDeliveryAddress as formatDeliveryAddressFor,
  formatHomeAddress,
  getRequisitionSubmitBlockers,
  INSUFFICIENT_BALANCE_COPY,
  makeEmptyRequisitionTool,
  makeRequisitionFormDefaultValues,
  mergeToolSetIntoTools,
  removeToolAt,
  type RequisitionFormData,
  requisitionFormSchema,
  requisitionPayerId,
} from './requisitionShared';
import {
  useIsRequisitionManager,
  useIsToolFundAdmin,
  useRequisitionBalance,
  useRequisitionCatalog,
  useRequisitionToolSets,
} from './useRequisitionShared';
import { WhoPaysChoice } from './WhoPaysChoice';

interface RequisitionFormProps {
  userId: number;
  onSubmit: (requisition: Requisition) => void;
  ignoreTradeRestriction?: boolean;
  isSubmitting?: boolean;
}

export const RequisitionForm = ({
  userId,
  onSubmit,
  ignoreTradeRestriction = false,
  isSubmitting = false,
}: RequisitionFormProps) => {
  const { user } = useAuth();
  const [copiedLinks, setCopiedLinks] = useState<Set<string>>(new Set());
  const [showToolSetSelection, setShowToolSetSelection] = useState(false);
  const isManager = useIsRequisitionManager();
  const isToolFundAdmin = useIsToolFundAdmin();

  const form = useForm<RequisitionFormData>({
    resolver: zodResolver(requisitionFormSchema),
    defaultValues: makeRequisitionFormDefaultValues(),
  });

  const selectedCategoryId = form.watch('tools.0.categoryId');

  const { userSettings, categories, catalogItems, categoryOptions, isCatalogItemsQueryPending } =
    useRequisitionCatalog({ userId, ignoreTradeRestriction, selectedCategoryId });

  // Watch the selected technician ID to determine whose address to show
  const selectedTechnicianId = useWatch({ control: form.control, name: 'selectedTechnicianId' });

  // Determine the effective user ID (selected technician or default userId)
  const effectiveTechnicianId = selectedTechnicianId || userId;

  // Fetch the target technician's user data for address display
  const { data: targetTechnician } = useUserQuery({
    filters: { id: effectiveTechnicianId },
    enabled: effectiveTechnicianId > 0,
  });

  // Use the target technician's data if available, otherwise fall back to logged-in user
  const displayUser = targetTechnician || user;

  const isForSomeoneElse = effectiveTechnicianId !== user.id;
  const requesteePays = useWatch({ control: form.control, name: 'requesteePays' });
  // Flag off: the old form, which checked the log owner's balance and sent no payer.
  const isAdminPurchaseOn = useFeatureFlag('tool-fund.admin-purchase');
  const payerId = isAdminPurchaseOn
    ? requisitionPayerId({
        requesterId: user.id,
        requesteeId: effectiveTechnicianId,
        requesteePays,
        isToolFundAdmin,
      })
    : userId;
  const payer = payerId === user.id ? user : displayUser;
  const canChoosePayer = isAdminPurchaseOn && isToolFundAdmin && isForSomeoneElse;

  // Tool set queries
  const selectedDepartmentId = form.watch('toolSetDepartmentId');
  const { toolSetsQuery, departmentOptions, toolSetOptions } =
    useRequisitionToolSets(selectedDepartmentId);

  const selectedItemId = form.watch('tools.0.catalogItemId');
  const selectedItem = useMemo(
    () => findCatalogItem(catalogItems, selectedItemId),
    [catalogItems, selectedItemId],
  );

  const tools = useWatch({ control: form.control, name: 'tools' });

  const { availableBalance, totalCost, exceedsBalance, newBalance } = useRequisitionBalance({
    userId: payerId,
    tools,
    catalogItems,
  });

  const confirmedDeliveryAddress = form.watch('confirmedDeliveryAddress');
  const deliveryMethod = useWatch({ control: form.control, name: 'deliveryMethod' });

  const isSubmitDisabled = useMemo(() => {
    return (
      getRequisitionSubmitBlockers({
        tools,
        exceedsBalance,
        deliveryMethod,
        confirmedDeliveryAddress,
        needsPayerChoice: canChoosePayer && requesteePays === undefined,
      }).length > 0
    );
  }, [
    tools,
    exceedsBalance,
    deliveryMethod,
    confirmedDeliveryAddress,
    canChoosePayer,
    requesteePays,
  ]);

  const formatDeliveryAddress = useCallback(
    (method: string, customAddress?: { name: string; address: string }) =>
      formatDeliveryAddressFor(method, customAddress, displayUser),
    [displayUser],
  );

  const handleSubmit = useCallback(
    (data: RequisitionFormData) => {
      if (exceedsBalance && data.internalPurchase === false) {
        return;
      }

      const deliveryAddress = formatDeliveryAddress(
        data.deliveryMethod,
        data.confirmedDeliveryAddress
          ? {
              name: data.confirmedDeliveryAddress.name,
              address: data.confirmedDeliveryAddress.address,
            }
          : undefined,
      );

      const requisition = buildRequisition({
        data,
        userId,
        catalogItems,
        deliveryAddress,
        payerId: isAdminPurchaseOn ? payerId : 0,
      });
      if (!requisition) return;

      onSubmit(requisition);
    },
    [
      exceedsBalance,
      onSubmit,
      userId,
      catalogItems,
      formatDeliveryAddress,
      payerId,
      isAdminPurchaseOn,
    ],
  );

  const addTool = useCallback(() => {
    const currentTools = form.getValues('tools');
    // Add a new empty tool entry
    form.setValue('tools', [...currentTools, makeEmptyRequisitionTool()]);
  }, [form]);

  const removeTool = useCallback(
    (index: number) => {
      const currentTools = form.getValues('tools');

      form.setValue('tools', removeToolAt(currentTools, index), {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form],
  );

  const handleAddToolSet = useCallback(
    (toolSetId: number) => {
      const selectedToolSet = toolSetsQuery.data?.results?.find((ts) => ts.id === toolSetId);
      if (!selectedToolSet) return;

      const currentTools = form.getValues('tools');

      form.setValue('tools', mergeToolSetIntoTools(currentTools, selectedToolSet), {
        shouldDirty: true,
        shouldTouch: true,
      });

      // Reset tool set selection
      form.setValue('toolSetId', undefined);
      form.setValue('toolSetDepartmentId', undefined);
    },
    [form, toolSetsQuery.data],
  );

  useEffect(() => {
    // Fix Radix Dialog pointer events issue
    setTimeout(() => (document.body.style.pointerEvents = ''), 0);
  }, []);

  useEffect(() => {
    const handleFocus = (event: FocusEvent) => {
      setTimeout(() => {
        const element = event.target as HTMLElement;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };

    const inputs = document.querySelectorAll('input');
    inputs.forEach((input) => {
      input.addEventListener('focus', handleFocus);
    });

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener('focus', handleFocus);
      });
    };
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-2 md:space-y-6">
        <Card className="border border-black bg-white">
          <CardHeader>
            <CardTitle>Select Tools</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Selected Tools Display */}
            {form.watch('tools').some((tool) => tool.catalogItemId) && (
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-600">
                  Selected Tools ({form.watch('tools').filter((tool) => tool.catalogItemId).length})
                </div>
                {form.watch('tools').map((tool, index) => {
                  if (!tool.catalogItemId) return null;

                  const selectedItem = findCatalogItem(catalogItems, tool.catalogItemId);
                  if (!selectedItem) return null;

                  const category = categories?.results?.find(
                    (cat) => cat.id === selectedItem.categoryId,
                  );
                  const basePrice = selectedItem.cost;
                  const finalCost = basePrice * tool.quantity;

                  return (
                    <Card
                      key={`tool-${tool.catalogItemId}-${index}`}
                      className="border border-gray-200 bg-white"
                    >
                      <CardHeader className="flex flex-row items-center justify-between pb-3">
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                            #{index + 1}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{selectedItem.name}</div>
                            {category && (
                              <div className="text-sm text-gray-500">({category.name})</div>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTool(index)}
                          disabled={isSubmitting}
                          className="h-6 w-6 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-gray-500">Base Price</div>
                            <div className="font-semibold text-gray-900">
                              ${basePrice.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Quantity</div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  const newValue = Math.max(1, tool.quantity - 1);
                                  form.setValue(`tools.${index}.quantity`, newValue);
                                }}
                                disabled={isSubmitting || tool.quantity <= 1}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="min-w-[20px] text-center font-semibold text-gray-900">
                                {tool.quantity}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  form.setValue(`tools.${index}.quantity`, tool.quantity + 1);
                                }}
                                disabled={isSubmitting}
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Final Cost</div>
                            <div className="font-semibold text-green-600">
                              ${finalCost.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        {selectedItem.externalLink && (
                          <div>
                            <div className="mb-1 text-sm text-gray-500">Product Link</div>
                            <div className="rounded border border-gray-200 px-1 py-1">
                              <div className="flex items-center justify-between">
                                <a
                                  href={selectedItem.externalLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80 block flex-1 break-all text-sm"
                                >
                                  {selectedItem.externalLink.length > 50
                                    ? `${selectedItem.externalLink.slice(0, 50)}...`
                                    : selectedItem.externalLink}
                                </a>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(
                                        selectedItem.externalLink,
                                      );
                                      // Show success feedback
                                      setCopiedLinks(
                                        (prev: Set<string>) =>
                                          new Set([...prev, selectedItem.externalLink]),
                                      );
                                      setTimeout(() => {
                                        setCopiedLinks((prev: Set<string>) => {
                                          const newSet = new Set(prev);
                                          newSet.delete(selectedItem.externalLink);
                                          return newSet;
                                        });
                                      }, 1500);
                                    } catch (err) {
                                      console.error('Failed to copy to clipboard:', err);
                                    }
                                  }}
                                  className={`ml-2 h-6 w-6 p-0 transition-colors ${
                                    copiedLinks.has(selectedItem.externalLink)
                                      ? 'text-green-600'
                                      : ''
                                  }`}
                                >
                                  {copiedLinks.has(selectedItem.externalLink) ? (
                                    <Check className="h-3 w-3" />
                                  ) : (
                                    <Copy className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Tool Selection Dropdowns - Show at bottom when there's an empty tool entry */}
            {form.watch('tools').some((tool) => !tool.catalogItemId) && (
              <div className="border-t pt-4">
                <div className="mb-3 text-sm font-medium text-gray-600">Add New Tool</div>
                <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
                  {(() => {
                    const emptyToolIndex = form
                      .watch('tools')
                      .findIndex((tool) => !tool.catalogItemId);
                    if (emptyToolIndex === -1) return null;

                    return (
                      <>
                        <FormField
                          control={form.control}
                          name={`tools.${emptyToolIndex}.categoryId`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel>Category (Optional)</FormLabel>
                              <Combobox
                                value={field.value?.toString() || ''}
                                onChange={(value) => {
                                  field.onChange(value ? Number(value) : undefined);
                                  form.resetField(`tools.${emptyToolIndex}.catalogItemId`);
                                }}
                                values={categoryOptions}
                                placeholder="Select a category..."
                                disabled={isSubmitting}
                              />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`tools.${emptyToolIndex}.catalogItemId`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel>Select Tool</FormLabel>
                              <Combobox
                                value={field.value?.toString() || ''}
                                onChange={(value) => {
                                  const catalogItemId = Number(value);

                                  // Check if this tool is already selected
                                  const currentTools = form.getValues('tools');
                                  const existingToolIndex = currentTools.findIndex(
                                    (tool) => tool.catalogItemId === catalogItemId,
                                  );

                                  if (existingToolIndex !== -1) {
                                    // Tool already exists - increment quantity
                                    const currentQuantity =
                                      currentTools[existingToolIndex].quantity;
                                    form.setValue(
                                      `tools.${existingToolIndex}.quantity`,
                                      currentQuantity + 1,
                                    );
                                    // Remove the empty tool slot
                                    form.setValue(
                                      'tools',
                                      currentTools.filter((_, i) => i !== emptyToolIndex),
                                    );
                                  } else {
                                    // Tool doesn't exist - add it normally
                                    field.onChange(catalogItemId);
                                    if (value) {
                                      form.setValue(`tools.${emptyToolIndex}.quantity`, 1);
                                    }
                                  }
                                }}
                                isLoading={isCatalogItemsQueryPending}
                                values={catalogItems}
                                placeholder={
                                  catalogItems.length === 0 &&
                                  !ignoreTradeRestriction &&
                                  userSettings?.tradeTypeId
                                    ? 'No tools available for your trade'
                                    : 'Search for a tool...'
                                }
                                disabled={
                                  isSubmitting || isCatalogItemsQueryPending || field.disabled
                                }
                              />
                            </FormItem>
                          )}
                        />
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Add Tool Button - Only show when no empty tool entry exists */}
            {!form.watch('tools').some((tool) => !tool.catalogItemId) && (
              <div className="flex justify-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTool}
                  disabled={isSubmitting}
                  className="text-sm"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Tool
                </Button>
              </div>
            )}

            {/* Tool Set Selection - Only visible to managers */}
            {isManager && (
              <div className="border-t border-gray-200 pt-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Quick Add: Tool Sets</h3>
                  <p className="text-sm text-gray-600">
                    Quickly add multiple tools by selecting a pre-configured tool set
                  </p>
                </div>

                {!showToolSetSelection ? (
                  <div className="flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowToolSetSelection(true)}
                      disabled={isSubmitting}
                      className="text-sm"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Tool Set
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="toolSetDepartmentId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Department (Optional)</FormLabel>
                            <FormControl>
                              <SimpleSelect
                                onChange={(value: string) => {
                                  field.onChange(value === 'all' ? undefined : Number(value));
                                  form.setValue('toolSetId', undefined); // Reset tool set when department changes
                                }}
                                values={departmentOptions}
                                selectedValue={field.value?.toString() ?? 'all'}
                                placeholder="All departments"
                                disabled={isSubmitting}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="toolSetId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Select Tool Set</FormLabel>
                            <FormControl>
                              <SimpleSelect
                                onChange={(value: string) =>
                                  field.onChange(value ? Number(value) : undefined)
                                }
                                values={toolSetOptions}
                                selectedValue={field.value?.toString() ?? ''}
                                placeholder={
                                  toolSetOptions.length === 0
                                    ? 'No tool sets available'
                                    : 'Choose a tool set'
                                }
                                disabled={
                                  isSubmitting ||
                                  toolSetsQuery.isPending ||
                                  toolSetOptions.length === 0
                                }
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="default"
                        onClick={() => {
                          const toolSetId = form.getValues('toolSetId');
                          if (toolSetId) {
                            handleAddToolSet(toolSetId);
                          }
                        }}
                        disabled={!form.watch('toolSetId') || isSubmitting}
                        className="flex-1"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Tool Set
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowToolSetSelection(false);
                          form.setValue('toolSetId', undefined);
                          form.setValue('toolSetDepartmentId', undefined);
                        }}
                        disabled={isSubmitting}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {isManager && (
          <Card className="border border-black bg-white">
            <CardHeader>
              <CardTitle>Tool Assigned To:</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="selectedTechnicianId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Technician</FormLabel>
                    <TechniciansComboboxPicker
                      onSelect={(selectedUser: User | null) => {
                        if (selectedUser) field.onChange(selectedUser.id);
                      }}
                      selected={field.value?.toString() || ''}
                      disabled={isSubmitting}
                      emptyLabel="Select a technician"
                      emptySearch="No technician found"
                      placeholder="Search employee"
                      filter={(technicianUser) =>
                        technicianUser.employeeDepartmentID === user.employeeDepartmentID
                      }
                    />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

        {isManager && (
          <Card className="border border-black bg-white">
            <CardHeader>
              <CardTitle>Purchase Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="internalPurchase"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Bought by Kalos</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="purchaseComments"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Comments (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Add any additional notes about the purchase..."
                        disabled={isSubmitting}
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        )}

        {/* Section 2: Review Tool Fund Balance */}

        {/* Section 3: Delivery Method */}
        <Card className="border border-black bg-white">
          <CardHeader>
            <CardTitle>Select Method of Delivery</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="deliveryMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Delivery Method</FormLabel>
                  <FormControl>
                    <Combobox
                      value={field.value}
                      onChange={field.onChange}
                      values={[
                        { label: 'Warehouse Pickup', value: 'warehouse' },
                        { label: 'Home Delivery', value: 'home' },
                        { label: 'Other Location', value: 'other' },
                      ]}
                      placeholder="Select delivery method..."
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  {field.value === 'home' && (
                    <div className="bg-muted mt-2 rounded-md border p-3">
                      <p className="font-medium">Home Address</p>
                      <p className="text-muted-foreground text-sm">
                        {formatHomeAddress(displayUser)}
                      </p>
                    </div>
                  )}
                  {field.value === 'warehouse' && (
                    <div className="bg-muted mt-2 rounded-md border p-3">
                      <p className="font-medium">Kalos Office</p>
                      <p className="text-muted-foreground text-sm">
                        236 Hatteras Ave, Clermont Fl, 34711
                      </p>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {deliveryMethod === 'other' && (
              <FormField
                control={form.control}
                name="confirmedDeliveryAddress"
                render={({ field }) => (
                  <FormItem className="static-on-mobile relative">
                    <FormLabel>Delivery Address</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <BusinessAutocompleteInput
                              onChange={(place) => {
                                field.onChange(
                                  place
                                    ? {
                                        placeId: place.placeId,
                                        name: place.name,
                                        address: place.address,
                                      }
                                    : undefined,
                                );
                              }}
                              value={field.value || null}
                              disabled={isSubmitting}
                            />
                          </div>
                          {field.value && (
                            <Button
                              variant="ghost"
                              size="icon"
                              type="button"
                              onClick={() => field.onChange(undefined)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {field.value && (
                          <div className="bg-muted rounded-md border p-3">
                            <p className="font-medium">{field.value.name}</p>
                            <p className="text-muted-foreground text-sm">{field.value.address}</p>
                          </div>
                        )}
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </CardContent>
        </Card>

        <Card className="border border-black bg-white">
          <CardHeader>
            <CardTitle>When do you need it?</CardTitle>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="requestDate"
              render={({ field }) => (
                <FormItem className="flex flex-col justify-between">
                  <FormLabel>Needed Date</FormLabel>
                  <FormControl>
                    <DateInput
                      value={field.value}
                      onChange={(date) => field.onChange(date)}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        {canChoosePayer && (
          <Flagged flag="tool-fund.admin-purchase">
            <Card className="border border-black bg-white">
              <CardHeader>
                <CardTitle>Who Pays</CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="requesteePays"
                  render={({ field }) => (
                    <WhoPaysChoice
                      requesteeName={`${displayUser.firstname} ${displayUser.lastname}`.trim()}
                      requesteePays={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  )}
                />
                {requesteePays === undefined && (
                  <p className="text-muted-foreground mt-3 text-sm">
                    Choose whose tool fund pays to submit the request.
                  </p>
                )}
              </CardContent>
            </Card>
          </Flagged>
        )}

        <Card className="border border-black bg-white">
          <CardHeader>
            <CardTitle>Review Tool Fund Balance</CardTitle>
            {isAdminPurchaseOn && (!canChoosePayer || requesteePays !== undefined) && (
              <p className="text-muted-foreground text-sm">
                Charged to {payer.firstname} {payer.lastname}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={cn(
                'grid items-center',
                selectedItem
                  ? 'grid-cols-1 gap-1 md:grid-cols-[1fr_20px_1fr_20px_1fr]'
                  : 'grid-cols-1 gap-4 md:grid-cols-3',
              )}
            >
              <div>
                <p className="text-muted-foreground text-sm font-medium">Current Balance</p>
                <p className="text-base font-bold md:text-2xl">${availableBalance.toFixed(2)}</p>
              </div>
              {selectedItem && (
                <>
                  <div className="text-lg font-bold md:text-center">-</div>
                  <div>
                    <p className="text-muted-foreground text-sm font-medium">Tool Cost</p>
                    <p className="text-base font-bold text-amber-500 md:text-2xl">
                      ${totalCost.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-lg font-bold md:text-center">=</div>
                  <div>
                    <p className="text-muted-foreground text-sm font-medium">New Balance</p>
                    <p
                      className={cn(
                        'text-base font-bold md:text-2xl',
                        exceedsBalance ? 'text-red-500' : 'text-green-500',
                      )}
                    >
                      ${newBalance.toFixed(2)}
                    </p>
                  </div>
                </>
              )}
            </div>

            {exceedsBalance && (
              <Alert variant="destructive">
                <AlertTitle>{INSUFFICIENT_BALANCE_COPY.title}</AlertTitle>
                <AlertDescription>{INSUFFICIENT_BALANCE_COPY.body}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isSubmitDisabled || isSubmitting}
            isLoading={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
